import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ScienceIcon from '@mui/icons-material/Science';
import {
  ChipField,
  Datagrid,
  FunctionField,
  ListContextProvider,
  ListControllerResult,
  TextField,
  useGetMany,
  useList,
  useListContext,
} from 'react-admin';

import ColoredScoreField from '../../../customFields/ColoredScoreField';
import StatusField from '../../../customFields/StatusField';
import Pagination from '../../layout/Pagination';
import { boldDataGridStyle } from '../../../constants/style/datagridStyles';
import { Ping } from '../../../utilities/schemas/ping';

const PINGS_PER_PAGE = 5;

const SelectedPingsList = () => {
  const { selectedIds, onToggleItem } = useListContext();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PINGS_PER_PAGE);

  const pageIds = selectedIds.slice((page - 1) * perPage, page * perPage);
  const { data, isLoading } = useGetMany<Ping>('pings', { ids: pageIds });

  const totalPages = Math.ceil(selectedIds.length / perPage);
  if (page > totalPages && totalPages > 0) {
    setPage(totalPages);
  }

  const listContext = useList({
    data: data || [],
    perPage,
    isFetching: isLoading,
    isLoading,
  });

  const paginationContext = {
    ...listContext,
    data: data || [],
    total: selectedIds.length,
    page,
    perPage,
    setPage,
    setPerPage,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,
  } as ListControllerResult<Ping>;

  if (selectedIds.length === 0) {
    return (
      <Box py={2} textAlign="center">
        <Typography color="text.secondary">No pings selected</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Selected pings (
        {selectedIds.length}
        )
      </Typography>
      <ListContextProvider value={paginationContext}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Datagrid
            sx={boldDataGridStyle}
            bulkActionButtons={false}
          >
            <TextField source="id" label="ID" sortable={false} />
            <TextField source="description" label="Description" sortable={false} />
            <StatusField source="status" label="Status" sortable={false} />
            <ColoredScoreField source="score" label="Current score" sortable={false} />
            <FunctionField
              label="Test"
              sortable={false}
              render={(record: Ping) => (
                record.isTestPing ? (
                  <ChipField
                    icon={<ScienceIcon />}
                    record={{ value: 'Test Ping' }}
                    color="primary"
                    source="value"
                    size="small"
                  />
                ) : null
              )}
            />
            <FunctionField
              label=""
              sortable={false}
              render={(record: Ping) => (
                <IconButton
                  size="small"
                  onClick={() => onToggleItem(record.id)}
                  aria-label={`Remove ping ${record.id}`}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            />
          </Datagrid>
        )}
        <Pagination rowsPerPageOptions={[5, 10, 25]} />
      </ListContextProvider>
    </Box>
  );
};

export default SelectedPingsList;
